import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/cn';
import { useHaptics } from '../../hooks/useHaptics';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../hooks/useQueries';
import { initial } from '../../lib/format';
import { NAV_ITEMS, type TabKey } from './TabBar';
import { LeagueSwitcher } from './LeagueSwitcher';
import { BrandMark } from './bits';

/** Desktop-only navigation rail. Hidden on mobile (the TabBar takes over there).
 *
 * Tablet band (`md`–`lg`, 768–1023px): slim icon rail (~72px) so iPad portrait
 * reclaims horizontal room for 2-column content. Full labelled rail from `lg`. */
export function SideNav({
  active = 'play',
  leagueId,
  onSelect
}: {
  active?: TabKey;
  leagueId?: string | null;
  onSelect?: (key: TabKey) => void;
}) {
  const navigate = useNavigate();
  const { impact } = useHaptics();
  const { uid } = useAuth();
  const { data: profile } = useProfile(uid);
  const name = profile?.displayName ?? 'Player';

  return (
    <aside className="hidden w-[72px] shrink-0 flex-col overflow-visible border-r border-black/5 bg-white/70 px-2 py-6 backdrop-blur md:flex lg:w-[264px] lg:px-5 lg:py-7">
      {/* brand */}
      <div className="flex items-center justify-center gap-3 px-0 lg:justify-start lg:px-2">
        <BrandMark size={40} />
        <span className="hidden text-[17px] font-extrabold tracking-tight text-ink lg:inline">
          Head<span className="text-accent-ink">To</span>Head
        </span>
      </div>

      {/* active league world — tap to switch */}
      {leagueId && (
        <div className="relative z-20 mt-5 px-0 lg:mt-6 lg:px-1">
          <LeagueSwitcher leagueId={leagueId} size="icon" className="lg:hidden" />
          <LeagueSwitcher leagueId={leagueId} size="sm" className="hidden lg:block" />
        </div>
      )}

      {/* nav */}
      <nav className={cn('flex flex-col gap-1', leagueId ? 'mt-3 lg:mt-4' : 'mt-7 lg:mt-9')}>
        {NAV_ITEMS.map(({ key, label, Icon }) => {
          const on = key === active;
          return (
            <button
              key={key}
              type="button"
              aria-label={label}
              title={label}
              onClick={() => {
                impact('light');
                onSelect?.(key);
              }}
              className={cn(
                'tap flex items-center justify-center gap-3 rounded-2xl px-0 py-[11px] text-left transition-colors active:opacity-70 lg:justify-start lg:px-3',
                on ? 'bg-fill text-ink' : 'text-ink2 hover:bg-fill/60 hover:text-ink'
              )}
            >
              <Icon width={21} height={21} strokeWidth={2} />
              <span className={cn('hidden text-[14px] lg:inline', on ? 'font-bold' : 'font-semibold')}>
                {label}
              </span>
            </button>
          );
        })}
      </nav>

      {/* user */}
      <button
        type="button"
        aria-label={`Profile — ${name}`}
        title={name}
        onClick={() => navigate(leagueId ? `/play/${leagueId}/profile` : '/profile')}
        className="mt-auto flex items-center justify-center gap-3 rounded-2xl border border-black/5 bg-white px-2 py-[11px] text-left shadow-tile transition-colors hover:bg-wash lg:justify-start lg:px-3"
      >
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-night2 to-night text-[14px] font-bold text-white">
          {initial(name)}
        </div>
        <div className="hidden min-w-0 lg:block">
          <div className="truncate text-[14px] font-bold tracking-snug text-ink">{name}</div>
          <div className="truncate text-[12px] font-medium text-ink2">View profile</div>
        </div>
      </button>
    </aside>
  );
}
